package com.partejar.app.core.session

import com.russhwolf.settings.Settings

@kotlinx.serialization.Serializable
data class SessionTokens(
    val accessToken: String,
    val refreshToken: String
)

enum class StoredSessionState {
    NONE,
    LEGACY,
    RECOVERABLE
}

interface SessionTokenStore {
    fun current(): SessionTokens?
    fun state(): StoredSessionState
    fun save(tokens: SessionTokens)
    fun clear()
}

class SettingsSessionTokenStore(
    private val settings: Settings
) : SessionTokenStore {

    override fun current(): SessionTokens? {
        val accessToken = settings.getStringOrNull(SessionKeys.ACCESS_TOKEN)
        val refreshToken = settings.getStringOrNull(SessionKeys.REFRESH_TOKEN)
        return if (!accessToken.isNullOrBlank() && !refreshToken.isNullOrBlank()) {
            SessionTokens(accessToken, refreshToken)
        } else {
            if (!accessToken.isNullOrBlank() || !refreshToken.isNullOrBlank()) clear()
            null
        }
    }

    override fun state(): StoredSessionState = when {
        current() != null -> StoredSessionState.RECOVERABLE
        !settings.getStringOrNull(SessionKeys.LEGACY_JWT_TOKEN).isNullOrBlank() -> StoredSessionState.LEGACY
        else -> StoredSessionState.NONE
    }

    override fun save(tokens: SessionTokens) {
        settings.putString(SessionKeys.ACCESS_TOKEN, tokens.accessToken)
        settings.putString(SessionKeys.REFRESH_TOKEN, tokens.refreshToken)
        settings.remove(SessionKeys.LEGACY_JWT_TOKEN)
    }

    override fun clear() {
        settings.remove(SessionKeys.ACCESS_TOKEN)
        settings.remove(SessionKeys.REFRESH_TOKEN)
        settings.remove(SessionKeys.LEGACY_JWT_TOKEN)
    }
}

/**
 * Moves the legacy Settings pair to a platform-secure store without ever deleting
 * the old pair before a successful read-back from the secure store.
 */
class MigratingSessionTokenStore(
    private val secureStore: SessionTokenStore,
    private val legacyStore: SessionTokenStore
) : SessionTokenStore {

    override fun current(): SessionTokens? {
        val secureTokens = secureStore.current()
        if (secureTokens != null) {
            legacyStore.clear()
            return secureTokens
        }

        val legacyTokens = legacyStore.current()
        if (legacyTokens == null) {
            // Also removes an unrecoverable JWT-only session and any partial pair.
            legacyStore.clear()
            return null
        }

        return try {
            secureStore.save(legacyTokens)
            secureStore.current()?.takeIf { it == legacyTokens }?.also {
                legacyStore.clear()
            } ?: legacyTokens
        } catch (_: Throwable) {
            // A failed secure write must never delete the still-usable legacy pair.
            legacyTokens
        }
    }

    override fun state(): StoredSessionState = when {
        current() != null -> StoredSessionState.RECOVERABLE
        else -> StoredSessionState.NONE
    }

    override fun save(tokens: SessionTokens) {
        secureStore.save(tokens)
        check(secureStore.current() == tokens) { "Não foi possível confirmar a sessão segura." }
        legacyStore.clear()
    }

    override fun clear() {
        secureStore.clear()
        legacyStore.clear()
    }
}
